import * as React from "react";
import { useState } from "react";
import { Container, Row, Col, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import logo from "../ui/navbar/logo.png";

const BASE_URL = "https://ea1-dev.azurewebsites.net";

type Step = "form" | "confirm" | "success";

export default function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<Step>("form");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const resolveAccount = async (): Promise<{ id: string; type: "requester" | "provider" } | null> => {
    // Try requester first
    try {
      const res = await axios.post(`${BASE_URL}/login/requester`, { email: email.toLowerCase(), password });
      if (res.data?.requester_id) {
        return { id: res.data.requester_id, type: "requester" };
      }
    } catch (e: any) {
      if (e.response?.status !== 404 && e.response?.status !== 401) throw e;
    }

    // Try provider
    try {
      const res = await axios.post(`${BASE_URL}/login/provider`, { email: email.toLowerCase(), password });
      if (res.data?.provider_id) {
        return { id: res.data.provider_id, type: "provider" };
      }
    } catch (e: any) {
      if (e.response?.status !== 404 && e.response?.status !== 401) throw e;
    }

    return null;
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setIsLoading(true);
    try {
      const account = await resolveAccount();
      if (!account) {
        setError("No account found with that email and password. Please check your credentials.");
        setIsLoading(false);
        return;
      }
      setStep("confirm");
    } catch (e: any) {
      setError("Something went wrong. Please try again.");
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    setError("");
    setIsLoading(true);
    try {
      const account = await resolveAccount();
      if (!account) {
        setError("Could not verify account. Please go back and try again.");
        setIsLoading(false);
        return;
      }

      const endpoint =
        account.type === "requester"
          ? `${BASE_URL}/api/userServiceRequesterBasic/${account.id}`
          : `${BASE_URL}/api/userServiceProviderBasic/${account.id}`;

      await axios.delete(endpoint);
      setStep("success");
    } catch (e: any) {
      setError("Failed to delete account. Please try again or contact support.");
    }
    setIsLoading(false);
  };

  return (
    <div
      style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}
      className="d-flex align-items-center justify-content-center"
    >
      <Container style={{ maxWidth: 480 }}>
        <Row>
          <Col>
            <div className="text-center mb-4">
              <img src={logo} width={72} height={61} alt="EA1 Roadside Assistance" />
              <h4 className="mt-3 mb-0">EA1 Roadside Assistance</h4>
            </div>

            {step === "form" && (
              <div style={{ backgroundColor: "#fff", padding: 32, borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
                <h5 className="mb-1">Delete Account</h5>
                <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                  Permanently deletes your account and all associated data including your profile,
                  vehicle information, service history, and emergency contacts.{" "}
                  <strong>This action cannot be undone.</strong>
                </p>

                {error && <Alert variant="danger" style={{ fontSize: 14 }}>{error}</Alert>}

                <Form onSubmit={handleVerify}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                    />
                  </Form.Group>
                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                  </Form.Group>
                  <Button
                    type="submit"
                    variant="danger"
                    className="w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? <Spinner animation="border" size="sm" /> : "Continue"}
                  </Button>
                </Form>
              </div>
            )}

            {step === "confirm" && (
              <div style={{ backgroundColor: "#fff", padding: 32, borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }}>
                <h5 className="mb-1">Are you sure?</h5>
                <p className="text-muted mb-4" style={{ fontSize: 14 }}>
                  You are about to permanently delete the account for{" "}
                  <strong>{email}</strong>. All of your data will be removed and
                  cannot be recovered.
                </p>

                {error && <Alert variant="danger" style={{ fontSize: 14 }}>{error}</Alert>}

                <Button
                  variant="danger"
                  className="w-100 mb-2"
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : "Yes, Delete My Account"}
                </Button>
                <Button
                  variant="outline-secondary"
                  className="w-100"
                  onClick={() => { setStep("form"); setError(""); }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            )}

            {step === "success" && (
              <div style={{ backgroundColor: "#fff", padding: 32, borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.1)" }} className="text-center">
                <h5 className="mb-3">Account Deleted</h5>
                <p className="text-muted" style={{ fontSize: 14 }}>
                  Your account and all associated data have been permanently deleted.
                  If you have any questions, contact us at{" "}
                  <a href="mailto:admin.support@ecare-software.com">
                    admin.support@ecare-software.com
                  </a>
                  .
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
}
